import type { BonusHistoryEntry, HistoryOrder } from '@snitch/contract'
import { cn } from '@snitch/ui/lib/cn'
import { MotionValue, SlidingSelection } from '@snitch/ui/motion'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  Check,
  Clock3,
  LoaderCircle,
  MessageCircle,
  ReceiptText,
  Wallet,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { ThemeToggle } from '../../../components/theme-toggle.tsx'
import { api } from '../../../lib/api.ts'
import { summaryQuery } from '../../../lib/billing.ts'
import {
  historyBonus,
  historyMoney,
  historyMonth,
  historyPeriod,
  historyPurchase,
  historyWhen,
} from '../../../lib/history.ts'

export const Route = createFileRoute('/_authed/_shell/profile/history')({
  validateSearch: (search: Record<string, unknown>): { view: 'orders' | 'bonuses' } => ({
    view: search.view === 'bonuses' ? 'bonuses' : 'orders',
  }),
  component: HistoryPage,
})

function HistoryPage() {
  const { view } = Route.useSearch()
  const { me } = Route.useRouteContext()
  return (
    <main className="flex flex-col gap-5 px-5 pt-[max(16px,var(--tg-viewport-content-safe-area-inset-top,0px))] pb-[max(24px,env(safe-area-inset-bottom))] md:gap-6 md:px-10 md:pt-7 md:pb-8 xl:px-14 xl:pt-8 xl:pb-10">
      <div className="flex h-11 items-center justify-between">
        <Link
          to="/profile"
          className="group flex min-h-11 items-center gap-2 rounded-full text-ink-2 text-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink"
        >
          <span className="flex size-11 items-center justify-center rounded-full bg-card text-ink transition-colors group-hover:bg-sunken">
            <ArrowLeft className="size-5" aria-hidden="true" />
          </span>
          Профиль
        </Link>
        <ThemeToggle className="hidden md:flex" />
      </div>
      <header className="flex flex-col gap-2">
        <h1 className="font-bold font-display text-[28px] leading-[1.25] tracking-[-1px] md:text-[32px] md:tracking-[-1.1px]">
          История
        </h1>
        <p className="text-ink-2 text-sm leading-[18px]">Заказы и движение бонусов</p>
      </header>
      <Balance userId={me.user.id} />
      <SlidingSelection
        active={view}
        tone="primary"
        className="w-full rounded-3xl bg-sunken md:w-80"
      >
        <nav aria-label="История операций" className="flex h-12 w-full gap-1 p-1">
          {(['orders', 'bonuses'] as const).map((tab) => (
            <Link
              key={tab}
              to="/profile/history"
              search={{ view: tab }}
              replace
              aria-current={view === tab ? 'page' : undefined}
              className={cn(
                'flex min-w-0 flex-1 items-center justify-center rounded-[20px] text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
                view === tab
                  ? 'bg-primary font-semibold text-on-primary'
                  : 'font-medium text-ink-2 hover:text-ink',
              )}
            >
              {tab === 'orders' ? 'Заказы' : 'Бонусы'}
            </Link>
          ))}
        </nav>
      </SlidingSelection>
      {view === 'orders' ? <Orders userId={me.user.id} /> : <Bonuses userId={me.user.id} />}
      <Link
        to="/support"
        className="flex min-h-11 w-fit items-center gap-2 rounded-lg text-ink-2 text-xs underline-offset-4 hover:text-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink md:text-[13px]"
      >
        <MessageCircle className="size-[18px] shrink-0" aria-hidden="true" />
        Вопрос по операции? Напишите нам
      </Link>
    </main>
  )
}

function Balance({ userId }: { userId: string }) {
  const query = useQuery({ ...summaryQuery, queryKey: [...summaryQuery.queryKey, userId] })
  return (
    <section
      aria-label="Бонусный баланс"
      className="flex min-h-[76px] items-center gap-3 rounded-[22px] surface px-5 py-4"
    >
      <Wallet className="size-6 shrink-0 text-violet" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <h2 className="font-semibold text-sm">Бонусный баланс</h2>
        {query.isError ? (
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="mt-[3px] text-ink-2 text-xs underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            Не удалось загрузить. Повторить
          </button>
        ) : (
          <p className="mt-[3px] text-ink-2 text-xs">Для следующих покупок</p>
        )}
      </div>
      <p className="shrink-0 font-semibold text-2xl tabular-nums md:text-[28px]" aria-live="polite">
        <MotionValue>{query.data ? historyMoney(query.data.bonusBalance) : '—'}</MotionValue>
      </p>
    </section>
  )
}

function Orders({ userId }: { userId: string }) {
  const query = useInfiniteQuery({
    queryKey: ['billing', 'orderHistory', userId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.billing.orderHistory({ before: pageParam, limit: 20 }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
  return (
    <Feed
      items={query.data?.pages.flatMap((page) => page.items) ?? []}
      query={query}
      kind="orders"
      renderItem={(item) => <OrderRow order={item} />}
    />
  )
}

function Bonuses({ userId }: { userId: string }) {
  const query = useInfiniteQuery({
    queryKey: ['billing', 'bonusHistory', userId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.billing.bonusHistory({ before: pageParam, limit: 20 }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
  return (
    <Feed
      items={query.data?.pages.flatMap((page) => page.items) ?? []}
      query={query}
      kind="bonuses"
      renderItem={(item) => <BonusRow entry={item} />}
    />
  )
}

interface FeedQuery {
  isFetching: boolean
  isFetchNextPageError: boolean
  isPending: boolean
  isError: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  refetch: () => Promise<unknown>
  fetchNextPage: () => Promise<unknown>
}

function Feed<T extends { id: string; createdAt: Date }>({
  items,
  query,
  kind,
  renderItem,
}: {
  items: T[]
  query: FeedQuery
  kind: 'orders' | 'bonuses'
  renderItem: (item: T) => ReactNode
}) {
  if (query.isPending)
    return (
      <p role="status" className="flex items-center gap-2 py-6 text-ink-2 text-sm">
        <LoaderCircle
          className="size-4 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
        Загружаем историю…
      </p>
    )
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const month = historyMonth(item.createdAt)
    const entries = groups.get(month)
    if (entries) entries.push(item)
    else groups.set(month, [item])
  }
  return (
    <div className="flex flex-col gap-4 md:gap-[22px]">
      {items.length === 0 && !query.isError ? <Empty kind={kind} /> : null}
      {[...groups].map(([month, entries]) => (
        <section key={month} className="flex flex-col gap-2.5" aria-label={month}>
          <h2 className="font-medium font-mono text-[11px] text-ink-3 uppercase tracking-[0.6px]">
            {month}
          </h2>
          <ul className="divide-y divide-line rounded-3xl bg-card px-4 md:px-5">
            {entries.map((item) => (
              <li key={item.id} className="py-3.5 md:py-5">
                {renderItem(item)}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {query.isError ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-3xl bg-card p-5 text-sm"
        >
          <p>
            {query.isFetchNextPageError
              ? 'Не удалось загрузить следующие операции.'
              : items.length
                ? 'Не удалось обновить историю.'
                : 'Не удалось загрузить историю.'}
          </p>
          <button
            type="button"
            disabled={query.isFetching}
            className="min-h-11 rounded-full bg-primary px-4 font-semibold text-on-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-50"
            onClick={() =>
              void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())
            }
          >
            Повторить
          </button>
        </div>
      ) : null}
      {query.hasNextPage && !query.isError ? (
        <button
          type="button"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
          className="min-h-11 self-center rounded-full bg-card px-5 font-semibold text-sm transition-colors hover:bg-sunken focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-50"
        >
          {query.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
        </button>
      ) : null}
    </div>
  )
}

function Empty({ kind }: { kind: 'orders' | 'bonuses' }) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-3xl bg-card px-6 py-10 text-center">
      <ReceiptText className="size-8 text-ink-3" aria-hidden="true" />
      <h2 className="font-semibold text-xl">
        {kind === 'orders' ? 'Заказов пока нет' : 'Операций с бонусами пока нет'}
      </h2>
      <p className="max-w-[340px] text-ink-2 text-sm leading-[1.5]">
        {kind === 'orders'
          ? 'После первой покупки здесь появятся стоимость, срок и способ оплаты.'
          : 'Здесь появятся начисления и списания бонусов с пояснениями.'}
      </p>
    </div>
  )
}

function Status({ status }: { status: HistoryOrder['status'] }) {
  const label = {
    paid: 'Оплачено',
    pending: 'Ждём оплату',
    cancelled: 'Отменён',
    expired: 'Срок оплаты истёк',
  }[status]
  return (
    <span
      className={cn(
        'inline-flex w-fit shrink-0 items-center gap-1 rounded-full px-2 py-1 font-semibold text-[11px] leading-[14px]',
        status === 'paid'
          ? 'bg-kb-green-soft text-kb-green-ink'
          : status === 'pending'
            ? 'bg-yellow-soft text-on-yellow-soft'
            : 'bg-sunken text-ink-2',
      )}
    >
      {status === 'paid' ? (
        <Check className="size-3" aria-hidden="true" />
      ) : status === 'pending' ? (
        <Clock3 className="size-3" aria-hidden="true" />
      ) : null}
      {label}
    </span>
  )
}

function OrderRow({ order }: { order: HistoryOrder }) {
  const purchase = historyPurchase(order)
  const period = historyPeriod(order.period)
  const description = [purchase.detail, period].filter(Boolean).join(' · ')
  return (
    <div className="flex min-w-0 flex-col gap-2 xl:flex-row xl:items-center xl:gap-6">
      <div className="flex items-center justify-between gap-2 xl:hidden">
        <time
          dateTime={order.createdAt.toISOString()}
          className="text-ink-3 text-xs leading-[15px]"
        >
          {historyWhen(order.createdAt)}
        </time>
        <Status status={order.status} />
      </div>
      <div className="min-w-0 flex-1">
        <time
          dateTime={order.createdAt.toISOString()}
          className="mb-[5px] hidden text-ink-3 text-xs leading-[15px] xl:block"
        >
          {historyWhen(order.createdAt)}
        </time>
        <div className="flex items-start justify-between gap-3">
          <h3 className="min-w-0 break-words font-semibold text-[17px] leading-[22px]">
            {purchase.title}
            <span className="hidden xl:inline">
              {purchase.detail ? ` · ${purchase.detail}` : ''}
            </span>
          </h3>
          <p className="shrink-0 font-semibold text-xl tabular-nums xl:hidden">
            {historyMoney(order.quote.subtotal)}
          </p>
        </div>
        {description ? (
          <p className="mt-1.5 break-words text-ink-2 text-[13px] leading-[17px] xl:hidden">
            {description}
          </p>
        ) : null}
        {period ? (
          <p className="mt-[5px] hidden text-ink-2 text-[13px] leading-[17px] xl:block">{period}</p>
        ) : null}
      </div>
      <div className="hidden w-[102px] shrink-0 flex-col gap-[5px] xl:flex">
        <span className="text-ink-3 text-xs">Стоимость</span>
        <p className="font-semibold text-[22px] tabular-nums">
          {historyMoney(order.quote.subtotal)}
        </p>
      </div>
      <div className="text-ink-2 text-xs xl:w-[170px] xl:shrink-0">
        <span className="xl:block xl:font-medium xl:text-ink xl:text-sm">
          {historyMoney(order.quote.bonusSpent)} бонусами
        </span>
        <span className="xl:hidden"> · </span>
        <span className="xl:mt-[5px] xl:block">
          {historyMoney(order.quote.total)} деньгами{order.status !== 'paid' ? ' к оплате' : ''}
        </span>
      </div>
      <div className="hidden xl:block">
        <Status status={order.status} />
      </div>
    </div>
  )
}

function BonusRow({ entry }: { entry: BonusHistoryEntry }) {
  const copy = historyBonus(entry)
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <time dateTime={entry.createdAt.toISOString()} className="text-ink-3 text-xs">
          {historyWhen(entry.createdAt)}
        </time>
        <p
          className={cn(
            'shrink-0 font-semibold text-xl tabular-nums',
            entry.amount > 0 ? 'text-green-ink' : 'text-ink',
          )}
        >
          {entry.amount > 0 ? '+' : '−'}
          {historyMoney(Math.abs(entry.amount))}
        </p>
      </div>
      <h3 className="break-words font-semibold text-base leading-[21px]">{copy.title}</h3>
      {copy.detail ? (
        <p className="whitespace-pre-wrap break-words text-ink-2 text-[13px] leading-[1.4] [overflow-wrap:anywhere]">
          {copy.detail}
        </p>
      ) : null}
    </div>
  )
}
